
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { MapPin, Phone, Truck, Star, ArrowRight } from 'lucide-react';
import SEO from '@/components/SEO';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface CityMetauxPageProps {
  cityName: string;
  departmentName: string;
  departmentCode: string;
  region: string;
  description: string;
  specialities: string[];
  nearbyAreas: string[];
  heroImage?: string;
}

const CityMetauxPage: React.FC<CityMetauxPageProps> = ({
  cityName,
  departmentName,
  departmentCode,
  region,
  description,
  specialities,
  nearbyAreas,
  heroImage = "https://images.unsplash.com/photo-1605810230434-7631ac76ec81?w=1280&q=80"
}) => {
  const pageTitle = `Achat Métaux ${cityName} (${departmentCode}) - Rachat Fer Cuivre | Groupe Fabre`;
  const pageDescription = `Rachat de métaux à ${cityName} dans le ${departmentName}. Fer, cuivre, aluminium, inox. Pesée certifiée, paiement immédiat. Devis gratuit.`;
  const canonicalUrl = `/nos-services/achat-metaux/${cityName.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title={pageTitle}
        description={pageDescription}
        canonical={canonicalUrl}
        keywords={`achat métaux ${cityName}, rachat cuivre ${cityName}, ferraille ${departmentCode}, métaux ${departmentName}`}
        localBusiness={{
          city: cityName,
          region: departmentName,
          postalCode: `${departmentCode}000`,
          serviceType: "Achat et recyclage de métaux"
        }}
      />
      
      <Navbar />
      
      <main className="flex-1 mt-16">
        {/* Hero Section */}
        <div 
          className="relative bg-cover bg-center py-20"
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(44, 85, 48, 0.8), rgba(140, 198, 63, 0.7)), url('${heroImage}')`
          }}
        >
          <div className="container mx-auto px-4">
            <div className="max-w-4xl text-white">
              <div className="flex items-center mb-6">
                <MapPin className="h-8 w-8 mr-4" />
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold mb-2">
                    Achat Métaux {cityName}
                  </h1>
                  <p className="text-xl opacity-90">
                    Rachat de métaux ferreux et non ferreux dans le {departmentName}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 text-fabre-lightgreen">
                <span className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  {cityName}, {departmentName} ({departmentCode})
                </span>
                <span className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  05.65.67.82.05
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content Principal */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-fabre-gray mb-6">
                  Rachat de métaux à {cityName}
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  <strong>Groupe Fabre Environnement</strong> propose ses services d'achat de métaux 
                  à <strong>{cityName}</strong> et dans toute l'agglomération du {departmentName}. 
                  {description}
                </p>
                
                <h3 className="text-xl font-bold text-fabre-gray mb-4">
                  Nos spécialités à {cityName}
                </h3>
                <ul className="space-y-3 mb-8">
                  {specialities.map((speciality, index) => (
                    <li key={index} className="flex items-start">
                      <ArrowRight className="h-5 w-5 text-fabre-green mr-3 mt-1" />
                      <span>{speciality}</span>
                    </li>
                  ))}
                </ul>

                <div className="bg-fabre-lightgreen border border-fabre-green/20 p-6 rounded-lg">
                  <h4 className="font-bold text-fabre-darkgreen mb-3">
                    Zones d'intervention autour de {cityName}
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-fabre-gray text-sm">
                    {nearbyAreas.map((area, index) => (
                      <span key={index} className="flex items-center">
                        <MapPin className="h-3 w-3 mr-1" />
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-fabre-gray mb-6">
                  Service professionnel à {cityName}
                </h3>
                <p className="text-gray-600 mb-6">
                  Notre équipe d'experts intervient rapidement à {cityName} pour 
                  l'achat de tous types de métaux avec pesée certifiée et paiement immédiat.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center">
                    <Truck className="h-5 w-5 text-fabre-green mr-3" />
                    <span>Déplacement gratuit à {cityName}</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-fabre-green mr-3" />
                    <span>Pesée certifiée et transparente</span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-fabre-green mr-3" />
                    <span>Paiement immédiat</span>
                  </div>
                </div>

                <Link to="/contact" className="block">
                  <Button className="w-full bg-fabre-green hover:bg-fabre-darkgreen text-white">
                    Demander un devis à {cityName}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Types de métaux */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-fabre-gray mb-8 text-center">
              Types de métaux rachetés à {cityName}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { name: "Cuivre", desc: "Câbles, tuyaux, radiateurs", color: "bg-orange-100 text-orange-800" },
                { name: "Aluminium", desc: "Profilés, canettes, radiateurs", color: "bg-blue-100 text-blue-800" },
                { name: "Fer", desc: "Ferraille, poutres, machines", color: "bg-gray-100 text-gray-800" },
                { name: "Inox", desc: "Éviers, équipements industriels", color: "bg-green-100 text-green-800" },
                { name: "Laiton", desc: "Robinetterie, décorations", color: "bg-yellow-100 text-yellow-800" },
                { name: "Plomb", desc: "Batteries, tuyaux, plaques", color: "bg-purple-100 text-purple-800" }
              ].map((metal, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                  <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-3 ${metal.color}`}>
                    {metal.name}
                  </div>
                  <p className="text-gray-600 text-sm">{metal.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 bg-fabre-green text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Prêt à valoriser vos métaux à {cityName} ?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Contactez-nous pour une estimation gratuite et un service professionnel
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="secondary" size="lg" className="bg-white text-fabre-green hover:bg-gray-100">
                  Demander un devis
                </Button>
              </Link>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-fabre-green">
                Appeler maintenant
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CityMetauxPage;
